import axios from 'axios';
import { Decimal } from 'decimal.js-light';
import { DataUrl } from 'types/domainModels';
import { getEnvVar } from 'util/env';

const API_HOST = getEnvVar('API_BASE_URL');

export interface OrganizationCredits {
  organizationId: number;
  totalCreditAmount: string;
}

export function getBillingAPIUrl({
  path,
  version,
}: {
  path: string;
  version: 'v1';
}) {
  return `${API_HOST}/api/billing/${version}${path}`;
}

export type BillingServiceName =
  | 'FIELD_MANAGEMENT'
  | 'SURVEY_REVIEW'
  | 'SURVEY_PROGRAMMING'
  | 'QUESTIONNAIRE_DESIGN'
  | 'FULL_SERVICE'
  | 'EXTENDED_DECK'
  | 'SIGNATURE_DECK'
  | 'FOCUSED_REPORT'
  | 'SCORECARD'
  | 'TABLE_CREATION'
  | 'METHODOLOGY_APPROACH_CONSULTATION';

export interface BillingServiceCostsByLength {
  LENGTH_1_20: string;
  LENGTH_21_40: string;
  LENGTH_41_60: string;
}

export interface BillingServicePrice {
  service: BillingServiceName;
  cost: string | null;
  costsByLength: BillingServiceCostsByLength | null;
}

export const LIST_BILLING_SERVICES = () => ({
  path: '/services/list',
  version: 'v1' as const,
});

export async function listBillingServices(): Promise<BillingServicePrice[]> {
  const response = await axios.get<BillingServicePrice[]>(
    getBillingAPIUrl(LIST_BILLING_SERVICES()),
  );
  return response.data;
}

export type BillingPriceGroup =
  | 'SAMPLE'
  | 'SURVEY_CREATION'
  | 'FIELD_MANAGEMENT'
  | 'REPORTING';

export interface BillingPrice {
  priceName: string;
  price: string;
}

export interface ListPricesParams {
  isActive?: boolean;
  group?: BillingPriceGroup;
  surveyIncidence?: number;
  surveyLength?: number;
  version?: number;
}

export const LIST_PRICES = () => ({
  path: '/price',
  version: 'v1' as const,
});

export async function listPrices(
  params?: ListPricesParams,
): Promise<BillingPrice[]> {
  const response = await axios.get<BillingPrice[]>(
    getBillingAPIUrl(LIST_PRICES()),
    { params },
  );
  return response.data;
}

export interface BillingMultiplierOption {
  id: number;
  name: string;
  sortOrder: number;
  isDefault: boolean;
  multiplier: number;
}

export interface BillingMultiplier {
  id: number;
  name: string;
  isActive: boolean;
  version: number;
  options: BillingMultiplierOption[];
}

export interface ListMultipliersParams {
  isActive?: boolean;
}

export const LIST_MULTIPLIERS = () => ({
  path: '/multiplier',
  version: 'v1' as const,
});

export async function listMultipliers(
  params?: ListMultipliersParams,
): Promise<BillingMultiplier[]> {
  const response = await axios.get<BillingMultiplier[]>(
    getBillingAPIUrl(LIST_MULTIPLIERS()),
    { params },
  );
  return response.data;
}

export async function retrieveOrganizationCredits({
  orgId,
}: {
  orgId: number;
}): Promise<OrganizationCredits> {
  const response = await axios.get<OrganizationCredits>(
    getBillingAPIUrl({
      path: `/credit/organization/${orgId}`,
      version: 'v1',
    }),
  );
  return response.data;
}

export type BillingCreditTransactionType = 'CREDIT' | 'DEBIT';

export interface OrganizationCreditTransactionsUser {
  id: number;
  firstName: string;
  lastName: string;
  image: DataUrl | Record<string, unknown>;
}

export interface OrganizationCreditTransactionsItem {
  transactionType: BillingCreditTransactionType;
  transactionAmount: number;
  totalCreditAmount: number;
  user: OrganizationCreditTransactionsUser;
  invoiceId: number | null;
  surveyId: number | null;
  description: string | null;
  transactionDate: string;
}

export interface OrganizationCreditTransactions {
  transactions: OrganizationCreditTransactionsItem[];
  totalCount: number;
}

export async function retrieveOrganizationCreditTransactions({
  orgId,
  page,
  limit,
}: {
  orgId: number;
  page?: number;
  limit?: number;
}): Promise<OrganizationCreditTransactions> {
  const response = await axios.get<OrganizationCreditTransactions>(
    getBillingAPIUrl({
      path: `/credit/organization/${orgId}/transactions`,
      version: 'v1',
    }),
    {
      params: { page, limit },
    },
  );
  return response.data;
}

export async function addOrganizationCredits({
  organizationId,
  creditAmount,
  description,
}: {
  organizationId: number;
  creditAmount: Decimal;
  description?: string;
}): Promise<OrganizationCredits> {
  const response = await axios.post<OrganizationCredits>(
    getBillingAPIUrl({
      path: `/credit`,
      version: 'v1',
    }),
    {
      organizationId,
      creditAmount: creditAmount.toNumber(),
      description,
    },
  );
  return response.data;
}

export const CHARGE_ORGANIZATION = () => ({
  path: '/credit/charge',
  version: 'v1' as const,
});

export interface ChargeOrganizationRequest {
  organizationId: number;
  amount: number;
  email: string;
  stripeToken: string;
  description?: string;
}

export interface ChargeOrganizationResponse {
  amountCharged: string;
  creditsAdded: string;
}

export async function chargeOrganization({
  organizationId,
  amount,
  email,
  stripeToken,
  description,
}: ChargeOrganizationRequest): Promise<ChargeOrganizationResponse> {
  const response = await axios.post<ChargeOrganizationResponse>(
    getBillingAPIUrl(CHARGE_ORGANIZATION()),
    {
      organizationId,
      amount,
      email,
      stripeToken,
      description,
    },
  );
  return response.data;
}
